<template>
  <div class="page-shell">
    <p
      class="mobile-page-title font-helvetica-700 font-bold text-gray-dark lg:text-[40px] lg:uppercase"
    >
      Заказы
    </p>
    <OrdersNavBar />

    <div class="flex flex-col gap-4">
      <OrdersSortFilter
        v-model:model-value="sortValue"
        :options="sortOptions"
      />

      <OrdersReservedTable
        :orders="orders"
        :totals="totals"
        :loading="loading"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useNuxtApp } from "#imports";
import OrdersNavBar from "~/components/orders/OrdersNavBar.vue";
import OrdersReservedTable from "~/components/orders/OrdersReservedTable.vue";
import OrdersSortFilter from "~/components/orders/OrdersSortFilter.vue";
import { normalizeMediaUrl } from "~/utils/normalizeMediaUrl";
import {
  getDiscountPercent,
  getPriceBadgeLabel,
  parsePriceNumber,
} from "~/utils/pricing";

usePageTitle("Товары в резерве");

type Option = { label: string; value: string };

const sortOptions: Option[] = [
  { label: "Сначала недорогие", value: "cheap-first" },
  { label: "Сначала дорогие", value: "expensive-first" },
  { label: "По наименованию (от А до Я)", value: "name-asc" },
  { label: "По наименованию (от Я до А)", value: "name-desc" },
];

const sortValue = ref<string>(sortOptions[0]?.value ?? "");
type ReservedItem = {
  id: string;
  image: string;
  title: string;
  reservedQty: number | string;
  reserveUntil: string;
  warehouse: string;
  statusMain: string;
  statusSecondary: string;
  quantity: string;
  volume: string;
  price: string;
  discountPrice?: string;
  discountPercent?: number | string;
  priceBadgeLabel?: string;
  sum: string;
};

type OrderGroup = {
  orderCode: string;
  orderDate: string;
  items: ReservedItem[];
};

const orders = ref<OrderGroup[]>([]);
const totals = ref<{ volume: string; sum: string }>({ volume: "", sum: "" });
const loading = ref(false);

const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp.$useOneCApi as (
  action: string,
  payload?: Record<string, any>,
) => Promise<any>;

const mapData = (res: any) => {
  const items = Array.isArray(res?.["Товары"]) ? res["Товары"] : [];
  const grouped: Record<string, any> = {};

  items.forEach((item: any, index: number) => {
    const orderCode = String(item?.НомерЗаказа ?? "—");
    if (!grouped[orderCode]) {
      grouped[orderCode] = {
        orderCode,
        orderDate: String(item?.ДатаЗаказа ?? ""),
        items: [],
      };
    }
    const basePrice = item?.Цена;
    const discountedPrice = item?.ЦенаСоСкидкой;
    const hasDiscount =
      parsePriceNumber(basePrice) > 0 &&
      parsePriceNumber(discountedPrice) > 0 &&
      parsePriceNumber(discountedPrice) < parsePriceNumber(basePrice);
    const discountPercent = hasDiscount
      ? parsePriceNumber(item?.СкидкаПроцент) ||
        getDiscountPercent(discountedPrice, basePrice) ||
        undefined
      : undefined;

    grouped[orderCode].items.push({
      id: String(item?.guid ?? `${orderCode}-${index}`),
      image: normalizeMediaUrl(item?.ПутьКФото) || "/logo.webp",
      title: String(item?.Наименование ?? ""),
      reservedQty: String(item?.Количество ?? ""),
      reserveUntil: String(item?.СрокРезерва ?? ""),
      warehouse: String(item?.СкладОтгрузки ?? ""),
      statusMain: String(item?.ТекущееСостояние ?? ""),
      statusSecondary: item?.ПросроченДней
        ? `Просрочен ${item.ПросроченДней} дн.`
        : String(item?.Состояние ?? ""),
      quantity: `${item?.Количество ?? ""} ${item?.ЕИ ?? ""}`.trim(),
      volume: item?.Объем ? `${item.Объем} м3` : "",
      price: String(hasDiscount ? discountedPrice : (basePrice ?? "")),
      discountPrice: hasDiscount ? String(basePrice ?? "") : "",
      discountPercent,
      priceBadgeLabel: getPriceBadgeLabel(item?.ЦеноваяГруппа),
      sum: String(item?.Сумма ?? ""),
    });
  });

  orders.value = Object.values(grouped);

  const resTotals = res?.["Итоги"] ?? {};
  totals.value = {
    volume: resTotals?.ИтогоОбъем ? `${resTotals.ИтогоОбъем} м3` : "",
    sum: String(resTotals?.ИтогоСумма ?? ""),
  };
};

const fetchReserved = async () => {
  loading.value = true;
  try {
    const res = await $useOneCApi("getReservedProducts");
    mapData(res);
    applySort();
  } catch (error) {
    console.error("Не удалось загрузить резервы", error);
    orders.value = [];
    totals.value = { volume: "", sum: "" };
  } finally {
    loading.value = false;
  }
};

const applySort = () => {
  const getNumber = (val: string | number | null | undefined) =>
    Number(
      String(val ?? "")
        .replace(/[^\d.,-]/g, "")
        .replace(",", "."),
    );

  const sortFn = (a: OrderGroup, b: OrderGroup) => {
    const aSum = getNumber(
      a.items.reduce((acc, item) => acc + getNumber(item.sum), 0),
    );
    const bSum = getNumber(
      b.items.reduce((acc, item) => acc + getNumber(item.sum), 0),
    );
    const aFirst = a.items[0];
    const bFirst = b.items[0];

    switch (sortValue.value) {
      case "cheap-first":
        return aSum - bSum;
      case "expensive-first":
        return bSum - aSum;
      case "name-asc":
        return (aFirst?.title ?? "").localeCompare(bFirst?.title ?? "");
      case "name-desc":
        return (bFirst?.title ?? "").localeCompare(aFirst?.title ?? "");
      default:
        return 0;
    }
  };

  orders.value = [...orders.value].sort(sortFn);
};

onMounted(fetchReserved);

watch(sortValue, () => {
  applySort();
});
</script>
